// Baselayer Background Checks — raw HTTP transport for UCC filings. Hooks in
// @/hooks/backgroundChecks/useBgcUccFiling wrap these with TanStack Query.

import { api } from "../client";

// ---- wire types ------------------------------------------------------

export type BgcUccFilingType =
  | "Ucc1"
  | "Ucc3Continuation"
  | "Ucc3Amendment"
  | "Ucc3Assignment"
  | "Ucc3Termination";

export type BgcUccFilingStatus =
  | "Draft"
  | "PendingApproval"
  | "Approved"
  | "FilingSubmitted"
  | "Filed"
  | "FilingRejected"
  | "Withdrawn";

export interface BgcUccFilingDto {
  id: string;
  backgroundCheckId: string | null;
  dealId: string | null;
  businessId: string | null;
  /** Set when the draft was started from a servicing loan's UCC tab. */
  loanId: string | null;
  /** False = standard 5-year term; true = long-term (30-year) filing. */
  isLongTerm: boolean;
  /** Servicing UccFiling this draft continues/amends, when applicable. */
  parentServicingFilingId: string | null;
  filingType: BgcUccFilingType;
  status: BgcUccFilingStatus;
  filingState: string | null;
  debtorLegalName: string;
  debtorType: string | null;
  debtorMailingLine1: string | null;
  debtorCity: string | null;
  debtorState: string | null;
  debtorZip: string | null;
  debtorJurisdictionOfOrg: string | null;
  securedPartyLegalName: string;
  securedPartyType: string | null;
  securedPartyMailingLine1: string | null;
  securedPartyCity: string | null;
  securedPartyState: string | null;
  securedPartyZip: string | null;
  collateralDescriptionType: string | null;
  collateralDescriptionText: string | null;
  draftedByUserId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  rejectedByUserId: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  baselayerFilingId: string | null;
  filingPdfDocumentId: string | null;
  servicingUccFilingId: string | null;
  filedAt: string | null;
  createdAt: string;
}

export interface BgcUccFilingEditDto {
  id: string;
  action: string;
  userId: string;
  timestamp: string;
  notes: string | null;
}

export interface BgcUccFilingDraftRequest {
  filingType: BgcUccFilingType;
  backgroundCheckId?: string | null;
  dealId?: string | null;
  businessId?: string | null;
  loanId?: string | null;
  /** Defaults false (standard 5-year term) server-side. */
  isLongTerm?: boolean;
  parentServicingFilingId?: string | null;
  filingState?: string | null;
  debtorLegalName: string;
  debtorType?: string | null;
  debtorMailingLine1?: string | null;
  debtorCity?: string | null;
  debtorState?: string | null;
  debtorZip?: string | null;
  debtorJurisdictionOfOrg?: string | null;
  securedPartyLegalName: string;
  securedPartyType?: string | null;
  securedPartyMailingLine1?: string | null;
  securedPartyCity?: string | null;
  securedPartyState?: string | null;
  securedPartyZip?: string | null;
  collateralDescriptionType?: string | null;
  collateralDescriptionText?: string | null;
}

export interface BgcUccFilingsListParams {
  dealId?: string;
  loanId?: string;
  status?: string;
}

// ---- transport -------------------------------------------------------

export const bgcUccFilingApi = {
  list: (params?: BgcUccFilingsListParams) =>
    api.get<BgcUccFilingDto[]>("/background-checks/ucc-filings", {
      dealId: params?.dealId,
      loanId: params?.loanId,
      status: params?.status,
    }),
  get: (id: string) =>
    api.get<BgcUccFilingDto>(`/background-checks/ucc-filings/${id}`),
  edits: (id: string) =>
    api.get<BgcUccFilingEditDto[]>(`/background-checks/ucc-filings/${id}/edits`),
  create: (body: BgcUccFilingDraftRequest) =>
    api.post<BgcUccFilingDto>("/background-checks/ucc-filings", body),
  update: (id: string, body: BgcUccFilingDraftRequest) =>
    api.put<BgcUccFilingDto>(`/background-checks/ucc-filings/${id}`, body),
  submitForApproval: (id: string) =>
    api.post<BgcUccFilingDto>(
      `/background-checks/ucc-filings/${id}/submit-for-approval`,
    ),
  approve: (id: string) =>
    api.post<BgcUccFilingDto>(`/background-checks/ucc-filings/${id}/approve`),
  reject: (id: string, reason: string) =>
    api.post<BgcUccFilingDto>(`/background-checks/ucc-filings/${id}/reject`, {
      reason,
    }),
  recordManualFilingId: (id: string, baselayerFilingId: string) =>
    api.post<BgcUccFilingDto>(
      `/background-checks/ucc-filings/${id}/record-manual-filing-id`,
      { baselayerFilingId },
    ),
  attachPdf: (id: string, documentId: string) =>
    api.post<BgcUccFilingDto>(
      `/background-checks/ucc-filings/${id}/attach-pdf`,
      { documentId },
    ),
};
